import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from 'contexts/AuthContext';
import { useAuthWithIntakeStatus } from 'hooks/useAuthWithIntakeStatus';
import { useFlowCompletionStatus } from 'hooks/useFlowCompletionStatus';
import { LoadingAnimation } from 'components/ui/loading-animation';
import {
  INTAKE_ASSESSMENT_FLOW_NAME,
  isIntakeOptionalAuthenticatedPath,
} from 'constants/flows';

export const INTAKE_ASSESSMENT_FLOW_ROUTE = `/flows/${INTAKE_ASSESSMENT_FLOW_NAME}`;

const ALLOW_AUTHENTICATED_PUBLIC_PATHS = new Set(['/reset-password', '/forgot-password']);

type GuardProps = {
  children: React.ReactNode;
};

const buildReturnPath = (location?: { pathname?: string; search?: string; hash?: string }) => {
  if (!location?.pathname) return null;
  return `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
};

export const LoadingScreen: React.FC = () => (
  <div
    className="fixed inset-0 z-20 flex items-center justify-center"
    data-testid="route-guard-loading-screen"
    data-launch-blocking=""
  >
    <LoadingAnimation size="lg" />
  </div>
);

export const PublicRoute: React.FC<GuardProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const state = location.state as { from?: Location } | undefined;

  if (isAuthenticated && !ALLOW_AUTHENTICATED_PUBLIC_PATHS.has(location.pathname)) {
    return <Navigate to={buildReturnPath(state?.from) || '/home'} replace />;
  }

  return <>{children}</>;
};

export const ProtectedRoute: React.FC<GuardProps> = ({ children }) => {
  const {
    isAuthenticated,
    isAuthLoading,
    authExitReason,
    hasCompletedIntake,
    intakeStatusLoading,
    intakeStatusError,
  } = useAuthWithIntakeStatus();
  const location = useLocation();

  if (isAuthLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{
          from: location,
          ...(authExitReason ? { authExitReason } : {}),
        }}
        replace
      />
    );
  }

  const pathname = decodeURIComponent(location.pathname);
  const intakeOptional = isIntakeOptionalAuthenticatedPath(pathname);

  if (!intakeOptional && intakeStatusLoading && !intakeStatusError) {
    return <LoadingScreen />;
  }

  if (
    !intakeOptional &&
    !hasCompletedIntake &&
    !pathname.includes(INTAKE_ASSESSMENT_FLOW_ROUTE) &&
    !intakeStatusError
  ) {
    return <Navigate to={INTAKE_ASSESSMENT_FLOW_ROUTE} replace />;
  }

  return <>{children}</>;
};

export const RequireIntakeRoute: React.FC<GuardProps> = ({ children }) => {
  const {
    completionStatus: intakeCompletionStatus,
    loading: intakeStatusLoading,
    error: intakeStatusError,
  } = useFlowCompletionStatus(INTAKE_ASSESSMENT_FLOW_NAME);
  const hasCompletedIntake = Boolean(intakeCompletionStatus?.completed_at);

  if (intakeStatusLoading && !intakeStatusError) {
    return <LoadingScreen />;
  }

  if (!hasCompletedIntake && !intakeStatusError) {
    return <Navigate to={INTAKE_ASSESSMENT_FLOW_ROUTE} replace />;
  }

  return <>{children}</>;
};

export const AdminRoute: React.FC<GuardProps> = ({ children }) => {
  const { isAuthenticated, user, isAuthLoading, authExitReason } = useAuth();
  const routeLocation = useLocation();

  if (isAuthLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{
          from: routeLocation,
          ...(authExitReason ? { authExitReason } : {}),
        }}
        replace
      />
    );
  }

  if (!user?.is_staff) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

export const OrganizationAdminRoute: React.FC<GuardProps> = ({ children }) => {
  const { isAuthenticated, user, isAuthLoading, authExitReason } = useAuth();
  const routeLocation = useLocation();

  if (isAuthLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{
          from: routeLocation,
          ...(authExitReason ? { authExitReason } : {}),
        }}
        replace
      />
    );
  }

  const canAccessOrganizationAdmin = Boolean(
    user?.is_staff || (user?.organization?.id && user?.organization_role === 'admin')
  );
  if (!canAccessOrganizationAdmin) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};
